import 'extensions/ui-components/x-date-input';
import 'extensions/ui-components/x-loader';
import {formatIntlDate, formatIntlDayOfWeek} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {createRef, ref} from 'lit/directives/ref.js';
import styles from './x-installation-date-selector.component.scss?as=stylesheet';

export class InstallationDateSelector extends LitElement {

	static styles = [styles];

	static get properties () {
		return {
			dateSelected: {
				attribute: 'date-selected',
				type: String
			},
			loading: {
				type: Boolean
			},
			modify: {
				type: Boolean
			},
			notes: {
				type: String
			},
			results: {
				type: Array
			},
			selected: {
				type: String
			},
			timeSelected: {
				type: String
			}
		};
	}

	dateInput = createRef();

	timeInput = createRef();

	notesInput = createRef();

	constructor () {
		super();

		this.dateSelected = '';
		this.loading = false;
		this.notes = '';
		this.results = [];
		this.selected = '';
		this.timeSelected = '';
	}

	async callApi (data) {
		const response = await fetch(window.mivaJS?.API_Url, {
			body: JSON.stringify(data),
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});

		return response;
	}

	// eslint-disable-next-line max-lines-per-function
	async getResults (data) {
		this.loading = true;

		const results = await this.callApi(data);

		if (!results.ok) {
			return {
				error: 'Error: Unable to load delivery times. Please contact General after completing your order to schedule your delivery.',
				success: 0
			};
		}

		return this.formatResults(await results.json());
	}


	// eslint-disable-next-line max-lines-per-function
	formatResults (results) {
		const formattedResults = [];

		/*
		 *[
		 *	{
		 *		formattedDate: "August 8, 2023",
		 *		datetime: "2023-08-04T14:00:00.566Z"
		 *		options: [
		 *			{
		 *				datetime: '2023-08-04T14:00:00.566Z',
		 *				formatted: '7:00 AM'
		 *			},
		 *			{
		 *				datetime: '2023-08-04T19:00:00.566Z',
		 *				formatted: '12:00 PM'
		 *			}
		 *		]
		 *	}
		 *]
		 */

		if (results.success !== 1) {
			return false;
		}

		if (results.success === 1 && results?.data?.statusCode) {
			return {
				error: results?.data?.message,
				success: 0
			};
		}

		for (const result of results.data) {
			const date = new Date(result.date);
			const formattedDate = formatIntlDate(date);
			const foundResult = formattedResults.findIndex((formattedResult) => formattedResult.formattedDate === formattedDate);
			const hour = date.getHours();
			const option = {
				datetime: result.date,
				formatted: `${hour <= 12 ? hour : hour - 12}:${date.getMinutes().toString().padStart(2, '0')} ${hour <= 11 ? 'AM' : 'PM'}`
			};

			if (foundResult >= 0) {
				if (formattedResults[foundResult].options.findIndex((formattedOption) => formattedOption.datetime === option.datetime) >= 0) {
					continue;
				}
				formattedResults[foundResult].options.push(option);
			} else {
				formattedResults.push({
					datetime: result.date,
					formattedDate: formattedDate,
					options: [option]
				});
			}
		}

		return formattedResults;
	}

	async firstUpdated () {
		if (this.modify && this.selected) {
			this.handleFirstModified();
			return;
		}

		const today = new Date();
		const tomorrow = new Date();
		const nextDay = new Date();
		tomorrow.setDate(today.getDate() + 2);
		nextDay.setDate(today.getDate() + 17);

		const formattedTomorrow = `${tomorrow.getFullYear()}-${tomorrow.getMonth() + 1}-${tomorrow.getDate()}`;
		const formattedNextDay = `${nextDay.getFullYear()}-${nextDay.getMonth() + 1 }-${nextDay.getDate()}`;

		const requestBody = {
			// eslint-disable-next-line camelcase
			date_end: formattedNextDay,
			// eslint-disable-next-line camelcase
			date_start: formattedTomorrow,
			function: 'GET_PROFORMA_DATES',
			// eslint-disable-next-line camelcase
			post_code: window.mivaJS.Proforma_PostCode,
			state: window.mivaJS.Proforma_Region,
			// eslint-disable-next-line camelcase
			time_slot: ''
		};

		this.results = await this.getResults(requestBody);
		this.loading = false;
	}

	formatResultDate (date) {
		const datetime = new Date(date);

		return html`
			${formatIntlDate(datetime)}
			<span class="formatted-result-delimiter" aria-hidden="true">&nbsp;</span>
			<span class="formatted-result-day-of-week">${formatIntlDayOfWeek(datetime)}</span>
		`;
	}

	renderResults () {
		if (this.results.success === 0) {
			return html`
				<div class="installation-results">
					<div class="error-message">${this.results.error}</div>
					<br />
				</div>
			`;
		}

		if (!this.results?.length) {
			return nothing;
		}

		return html`
			<div class="installation-results">
				${this.results.map(date =>this.renderResultElement(date))}
			</div>
		`;
	}

	renderResultElement (date) {
		return html`
			<div class="installation-result">
				<div class="installation-result-title">${this.formatResultDate(date.datetime)}</div>
				<div class="installation-result-options">
					${date.options.map(option => this.renderResultOption(option))}
				</div>
			</div>
		`;
	}

	renderResultOption (option) {
		const classes = {
			'installation-result-option': true,
			'installation-result-option__selected': option.datetime === this.selected
		};

		return html`
			<button class="${classMap(classes)}" @click="${() => this.handleResultOptionClick(option.datetime)}">${option.formatted}</button>
		`;
	}

	handleResultOptionClick (value) {
		if (this.selected === value) {
			return;
		}

		this.selected = value;
		this.triggerEvent();
	}

	async handleFormSubmit (event) {
		event.preventDefault();

		this.loading = true;

		const requestBody = {
			// eslint-disable-next-line camelcase
			date_end: this.dateSelected,
			// eslint-disable-next-line camelcase
			date_start: this.dateSelected,
			function: 'GET_PROFORMA_DATES',
			// eslint-disable-next-line camelcase
			post_code: window.mivaJS.Proforma_PostCode,
			state: window.mivaJS.Proforma_Region,
			// eslint-disable-next-line camelcase
			time_slot: this.timeSelected
		};

		this.results = await this.getResults(requestBody);

		this.loading = false;
	}

	handleTimeChange () {
		this.timeSelected = this.timeInput.value.value;
	}

	get formattedTimeSelected () {
		if (this.timeSelected === '8:00:00') {
			return `8:00 AM`;
		}

		if (this.timeSelected === '12:00:00') {
			return `12:00 PM`;
		}

		return '';
	}

	handleDateChange () {
		this.dateSelected = this.dateInput.value.value;
	}

	handleNotesChange () {
		if (this.notes === this.notesInput?.value?.value) {
			return;
		}

		this.notes = this.notesInput.value.value;
		this.triggerEvent();
	}

	triggerEvent () {
		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			detail: {
				installationDate: this.selected,
				notes: this.notes
			}
		}));
	}

	render () {
		return html`
			<div class="x-installation-date-selector">
				<h2 class="x-installation-date-selector-title">Schedule Installation</h2>
				<div class="x-installation-date-form-title">Find a Date and Time</div>

				<form class="x-installation-form" @submit="${this.handleFormSubmit}">
					<div class="x-installation-form-fields">
						<div class="x-installation-form-field x-installation-form-field--date">
							<x-date-input disable-weekends name="date" class="x-installation-date-input" placeholder="Installation Date" value="${this.dateTimeFormat}" @change="${this.handleDateChange}" ${ref(this.dateInput)} required></x-date-input>
						</div>
						<div class="x-installation-form-field x-installation-form-field--time">
							<label for="installation-time" class="x-installation-form-label x-installation-form-label--time">
								<span class="x-installation-form-label__icon" aria-hidden="true"></span>
								<span class="x-installation-form-label__prompt">
									${this.timeSelected ? this.formattedTimeSelected : 'Time'}
								</span>

								<select id="installation-time" name="time" class="x-installation-form-input" ${ref(this.timeInput)} @change="${this.handleTimeChange}" required>
									<option value="">Select One</option>
									<option value="8:00:00">8:00 AM</option>
									<option value="12:00:00">12:00 PM</option>
								</select>
							</label>
						</div>
					</div>
					<button class="x-installation-form-button" type="submit" ?disabled="${!this.timeSelected || !this.dateSelected || this.loading}">Go</button>
				</form>

				<div class="installation-results">
					${this.loading ? html`<x-loader class="loader" aspect-ratio-desktop="6/2" aspect-ratio-tablet="6/2" aspect-ratio="6/2"></x-loader>` : this.renderResults()}
				</div>

				<div class="installation-notes">
					<label for="installation-notes" class="installation-notes-label">Installation Notes</label>
					<textarea id="installation-notes" name="notes" class="installation-notes-input" ${ref(this.notesInput)} @change="${this.handleNotesChange}" rows="6">${this.notes}</textarea>
				</div>
			</div>
		`;
	}

	handleFirstModified () {
		const date = new Date(this.selected);
		const hour = date.getHours();
		const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
		const formattedTime = `${hour <= 12 ? hour : hour - 12}:${date.getMinutes().toString().padStart(2, '0')} ${hour <= 11 ? 'AM' : 'PM'}`;

		if (this.dateInput.value) {
			this.dateInput.value.value = formattedDate;
			this.dateInput.value.dispatchEvent(new Event('change'));
		}

		if (this.timeInput.value) {
			this.timeInput.value.value = formattedTime;
			this.timeInput.value.dispatchEvent(new Event('change'));
		}

		this.results = this.formatResults({
			data: [
				{
					date: this.selected
				}
			],
			success: 1
		});
	}

	get dateTimeFormat () {
		if (!this.deliveryDate) {
			return '';
		}

		const d = new Date(this.deliveryDate);
		return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}
}
customElements.define('x-installation-date-selector', InstallationDateSelector);
